/**
 * Supabase Preferences Repository
 *
 * Implements IPreferencesRepository for Supabase storage.
 * Handles user preferences and notification settings.
 *
 * @module repositories/supabase/SupabasePreferencesRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type { IPreferencesRepository } from '../interfaces/IPreferencesRepository';
import type { UserPreferences, ChartPreferences } from '../../types/preferences';
import type { NotificationPreferences } from '../../types/notifications';
import type { Database, Json } from '../../types/database.types';

type UserPrefsRow = Database['public']['Tables']['user_preferences']['Row'];
type UserPrefsInsert = Database['public']['Tables']['user_preferences']['Insert'];
type UserPrefsUpdate = Database['public']['Tables']['user_preferences']['Update'];
type NotifPrefsRow = Database['public']['Tables']['notification_preferences']['Row'];
type NotifPrefsInsert = Database['public']['Tables']['notification_preferences']['Insert'];
type UserDbUpdate = Database['public']['Tables']['users']['Update'];

// Import defaults
import { DEFAULT_PREFERENCES as PREFS_DEFAULTS } from '../../types/preferences';
import { DEFAULT_NOTIFICATION_PREFERENCES as NOTIF_DEFAULTS } from '../../types/notifications';

// =============================================================================
// Trading Style Mapping (app domain ↔ DB constraint values)
// =============================================================================

// DB constraint: trading_style IN ('day_trading', 'swing_trading', 'position_trading', 'scalping')
// App type:      'day' | 'swing' | 'position'

const TRADING_STYLE_TO_DB: Record<'day' | 'swing' | 'position', string> = {
  day: 'day_trading',
  swing: 'swing_trading',
  position: 'position_trading',
};

const TRADING_STYLE_FROM_DB: Record<string, 'day' | 'swing' | 'position'> = {
  day_trading: 'day',
  swing_trading: 'swing',
  position_trading: 'position',
};

function tradingStyleToDb(style: 'day' | 'swing' | 'position' | undefined | null): string | null {
  if (!style) return null;
  return TRADING_STYLE_TO_DB[style] ?? style;
}

function tradingStyleFromDb(dbValue: string | null | undefined): 'day' | 'swing' | 'position' | undefined {
  if (!dbValue) return undefined;
  return TRADING_STYLE_FROM_DB[dbValue] ?? undefined;
}

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to UserPreferences domain object.
 */
function rowToPreferences(row: UserPrefsRow | null): UserPreferences {
  if (!row) {
    return { ...PREFS_DEFAULTS };
  }

  const chartDefaults = (row.chart_defaults as unknown as ChartPreferences) ?? PREFS_DEFAULTS.chart;

  return {
    theme: (row.theme as UserPreferences['theme']) ?? PREFS_DEFAULTS.theme,
    lastRoute: PREFS_DEFAULTS.lastRoute, // Not stored in DB, use default
    chart: {
      defaultSymbol: chartDefaults?.defaultSymbol ?? PREFS_DEFAULTS.chart.defaultSymbol,
      defaultTimeframe: chartDefaults?.defaultTimeframe ?? PREFS_DEFAULTS.chart.defaultTimeframe,
      defaultRange: chartDefaults?.defaultRange ?? PREFS_DEFAULTS.chart.defaultRange,
    },
    hasCompletedOnboarding: true, // Derived from presence in user_preferences
    accountBalance: row.account_balance ?? undefined,
    tradingStyle: tradingStyleFromDb(row.trading_style),
    tradingExperience: (row.trading_experience as UserPreferences['tradingExperience']) ?? undefined,
  };
}

/**
 * Convert database row to NotificationPreferences domain object.
 */
function rowToNotificationPrefs(row: NotifPrefsRow | null): NotificationPreferences {
  if (!row) {
    return { ...NOTIF_DEFAULTS };
  }

  return {
    emailEnabled: row.email_trade_reminders ?? NOTIF_DEFAULTS.emailEnabled,
    weeklyReportEmail: row.email_weekly_reports ?? NOTIF_DEFAULTS.weeklyReportEmail,
    tradingInsightsEmail: row.email_educational ?? NOTIF_DEFAULTS.tradingInsightsEmail,
    productUpdatesEmail: row.email_educational ?? NOTIF_DEFAULTS.productUpdatesEmail,
    dailyJournalReminder: NOTIF_DEFAULTS.dailyJournalReminder, // Not in DB schema
    marketOpenReminder: NOTIF_DEFAULTS.marketOpenReminder, // Not in DB schema
    marketCloseReminder: NOTIF_DEFAULTS.marketCloseReminder, // Not in DB schema
    marketingEmails: row.email_marketing ?? NOTIF_DEFAULTS.marketingEmails,
  };
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabasePreferencesRepository implements IPreferencesRepository {
  // ===========================================================================
  // User Preferences
  // ===========================================================================

  async getPreferences(userId: string): Promise<UserPreferences> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch preferences: ${error.message}`);
    }

    return rowToPreferences(data);
  }

  async savePreferences(userId: string, preferences: UserPreferences): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const row: UserPrefsInsert = {
      user_id: userId,
      account_balance: preferences.accountBalance ?? null,
      trading_style: tradingStyleToDb(preferences.tradingStyle),
      trading_experience: preferences.tradingExperience ?? null,
      theme: preferences.theme ?? null,
      chart_defaults: (preferences.chart as unknown as Json) ?? null,
    };

    const { error } = await supabase
      .from('user_preferences')
      .upsert(row, { onConflict: 'user_id' });

    if (error) throw new Error(`Failed to save preferences: ${error.message}`);
  }

  async updatePreferences(userId: string, updates: Partial<UserPreferences>): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // First, ensure the preferences row exists
    const existing = await this.getPreferences(userId);

    const updateData: UserPrefsUpdate = {};

    if (updates.accountBalance !== undefined) updateData.account_balance = updates.accountBalance;
    if (updates.tradingStyle !== undefined) updateData.trading_style = tradingStyleToDb(updates.tradingStyle);
    if (updates.tradingExperience !== undefined) updateData.trading_experience = updates.tradingExperience;
    if (updates.theme !== undefined) updateData.theme = updates.theme;
    if (updates.chart !== undefined) {
      // Merge with existing chart preferences
      updateData.chart_defaults = {
        ...existing.chart,
        ...updates.chart,
      } as unknown as Json;
    }

    const upsertRow: UserPrefsInsert = {
      user_id: userId,
      ...updateData,
    };

    const { error } = await supabase
      .from('user_preferences')
      .upsert(upsertRow, { onConflict: 'user_id' });

    if (error) throw new Error(`Failed to update preferences: ${error.message}`);
  }

  // ===========================================================================
  // Onboarding
  // ===========================================================================

  async completeOnboarding(
    userId: string,
    data: {
      accountBalance: number;
      tradingStyle: 'day' | 'swing' | 'position';
      tradingExperience: 'beginner' | 'intermediate' | 'advanced';
      tradingGoals?: string[];
    }
  ): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Update user_preferences with onboarding data
    const prefsRow: UserPrefsInsert = {
      user_id: userId,
      account_balance: data.accountBalance,
      trading_style: tradingStyleToDb(data.tradingStyle),
      trading_experience: data.tradingExperience,
      chart_defaults: PREFS_DEFAULTS.chart as unknown as Json,
      theme: PREFS_DEFAULTS.theme,
    };

    const { error: prefsError } = await supabase
      .from('user_preferences')
      .upsert(prefsRow, { onConflict: 'user_id' });

    if (prefsError) throw new Error(`Failed to save onboarding preferences: ${prefsError.message}`);

    // Also update the users table to mark onboarding complete
    const userUpdateData: UserDbUpdate = {
      onboarding_completed: true,
      onboarding_version: 1,
      updated_at: new Date().toISOString(),
    };

    const { error: userError } = await supabase
      .from('users')
      .update(userUpdateData)
      .eq('id', userId);

    if (userError) throw new Error(`Failed to update user onboarding status: ${userError.message}`);
  }

  // ===========================================================================
  // Notification Preferences
  // ===========================================================================

  async getNotificationPreferences(userId: string): Promise<NotificationPreferences> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch notification preferences: ${error.message}`);
    }

    return rowToNotificationPrefs(data);
  }

  async updateNotificationPreferences(
    userId: string,
    updates: Partial<NotificationPreferences>
  ): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const updateData: NotifPrefsInsert = {
      user_id: userId,
    };

    if (updates.emailEnabled !== undefined) updateData.email_trade_reminders = updates.emailEnabled;
    if (updates.weeklyReportEmail !== undefined) updateData.email_weekly_reports = updates.weeklyReportEmail;
    if (updates.tradingInsightsEmail !== undefined) updateData.email_educational = updates.tradingInsightsEmail;
    if (updates.productUpdatesEmail !== undefined) updateData.email_educational = updates.productUpdatesEmail;
    if (updates.marketingEmails !== undefined) updateData.email_marketing = updates.marketingEmails;

    const { error } = await supabase
      .from('notification_preferences')
      .upsert(updateData, { onConflict: 'user_id' });

    if (error) throw new Error(`Failed to update notification preferences: ${error.message}`);
  }
}
